import type {UseFilterParams} from '@/modules/common/hooks/useFilterParams';
import type {MetaFilters} from '@/modules/common/models/filters';
import type {
  Meta,
  MetaCategories,
  MetaRatings,
  MetaTourOperatorSpecial,
  PromotionCategory,
  MetaAttributeGroup,
} from '@/redux/v1/models';

import {mapValues, merge, sortBy} from 'lodash';

import {
  RatingCategories,
  type RatingCategory,
} from '@/modules/common/context/QueryParams/models';
import {
  FILTER_BEACH,
  FILTER_CHILDREN,
  FILTER_ENTERTAINMENT,
  FILTER_FACILITIES,
  FILTER_HOTEL_CATEGORY,
  FILTER_HOTEL_RATING,
  FILTER_HOTEL_TYPE,
  FILTER_POOL,
  FILTER_PROMOTION_FLEX,
  FILTER_PROMOTION_SAFETY,
  FILTER_PROMOTION_SPECIALS,
  FILTER_RESTAURANT,
  FILTER_SPORTS,
  FILTER_SUSTAINABILITY,
  FILTER_WATER_SPORTS,
  FILTER_WELLNESS,
} from '@/modules/common/models/filters';
import {
  getRatingCategoryThreshold,
  getRatingCategoryMaximum,
} from '@/modules/common/utils/rating';
import {
  PROMOTION_CATEGORY_FLEX,
  PROMOTION_CATEGORY_SAFETY,
  PROMOTION_CATEGORY_SPECIAL,
} from '@/redux/v1/models';
import {isMainType} from '@/utils/inviaType';

type Props = {
  filterParams: UseFilterParams;
  metaUnfiltered?: null | Meta;
  metaFiltered?: null | Meta;
};

type Result = Array<MetaFilters>;

const attributeFilterIdByItemType = {
  // @TODO use consts
  'IT03-HOT': FILTER_FACILITIES,
  'IT03-CHI': FILTER_CHILDREN,
  'IT03-WELL': FILTER_WELLNESS,
  'IT03-POOL': FILTER_POOL,
  'IT03-BEA': FILTER_BEACH,
  'IT03-SPO': FILTER_SPORTS,
  'IT03-WSPO': FILTER_WATER_SPORTS,
  'IT03-REST': FILTER_RESTAURANT,
  'IT03-ANI': FILTER_ENTERTAINMENT,
  'IT03-TYPE': FILTER_HOTEL_TYPE,
  'IT03-ECO': FILTER_SUSTAINABILITY,
};

const tourOperatorSpecialFilterIdByItemType = {
  [PROMOTION_CATEGORY_SPECIAL]: FILTER_PROMOTION_SPECIALS,
  [PROMOTION_CATEGORY_FLEX]: FILTER_PROMOTION_FLEX,
  [PROMOTION_CATEGORY_SAFETY]: FILTER_PROMOTION_SAFETY,
};

const filterIdByItemType = {
  categories: FILTER_HOTEL_CATEGORY,
  ratings: FILTER_HOTEL_RATING,
  ...attributeFilterIdByItemType,
  ...tourOperatorSpecialFilterIdByItemType,
} as const;
// @TODO use consts
const inviaTypeByPromotionCategory = {
  [PROMOTION_CATEGORY_SPECIAL]: 'IT08P',
  [PROMOTION_CATEGORY_FLEX]: 'IT08F',
  [PROMOTION_CATEGORY_SAFETY]: 'IT08S',
};

export const useMetaFilters = ({
  filterParams: [filterParams, setFilterParams],
  metaUnfiltered,
  metaFiltered,
}: Props): Result => {
  const getParamValueByInviaType = (
    paramName: 'hotelAttributes' | 'specials',
    inviaType: string
  ) => {
    const allValues = filterParams[paramName] || [];
    const filterValue = allValues.filter((v) => isMainType(v, inviaType));

    return {allValues, filterValue};
  };

  const attributeGroupToFilter = ({
    id,
    type,
    attributeGroupUnfiltered,
    attributeGroupFiltered,
  }: {
    id: string;
    type: string;
    attributeGroupUnfiltered: MetaAttributeGroup | undefined;
    attributeGroupFiltered: MetaAttributeGroup | undefined;
  }) => {
    const {allValues, filterValue} = getParamValueByInviaType(
      'hotelAttributes',
      type
    );
    const options = sortBy(
      Object.keys(attributeGroupUnfiltered?.items || {}).map((attributeId) => {
        const attributeUnfiltered =
          attributeGroupUnfiltered?.items[attributeId];
        const attributeFiltered = attributeGroupFiltered?.items[attributeId];

        return {
          value: attributeId,
          label: attributeUnfiltered?.translation || '',
          count: attributeFiltered?.count || 0,
          priceMin: attributeFiltered?.priceDisplay,
          active: filterValue.includes(attributeId),
          available: attributeFiltered != null,
        };
      }),
      (option) => option.label.toLowerCase()
    );

    return {
      id,
      title: attributeGroupUnfiltered?.translation || '',
      active: filterValue.length > 0,
      available: options.length > 0,
      value: filterValue,
      options,
      setValue: (value: Array<string>) => {
        const nextValue = allValues
          .filter((v) => !filterValue.includes(v))
          .concat(value);

        setFilterParams((previous) => ({
          ...previous,
          hotelAttributes: nextValue,
        }));
      },
      clear: () => {
        // @TODO can't remove all, need to toggle the activeOptions?
        setFilterParams((previous) => ({
          ...previous,
          hotelAttributes: null,
        }));
      },
    };
  };

  const specialGroupToFilter = ({
    id,
    type,
    specialGroupUnfiltered,
    specialGroupFiltered,
  }: {
    id: string;
    type: PromotionCategory;
    specialGroupUnfiltered: MetaTourOperatorSpecial | undefined;
    specialGroupFiltered: MetaTourOperatorSpecial | undefined;
  }) => {
    const {allValues, filterValue} = getParamValueByInviaType(
      'specials',
      inviaTypeByPromotionCategory[type]
    );
    const options = (specialGroupUnfiltered?.variations || []).map(
      (variationUnfiltered) => {
        const variationFiltered = specialGroupFiltered?.variations?.find(
          (variation) => variation.type === variationUnfiltered.type
        );

        return {
          value: variationUnfiltered.type,
          label: variationUnfiltered.translation || '',
          description: variationUnfiltered.tooltip,
          count: variationFiltered?.count,
          priceMin: undefined,
          active: filterValue.includes(variationUnfiltered.type),
          available: variationFiltered != null,
          tourOperators: variationUnfiltered?.tourOperators,
        };
      }
    );

    return {
      id,
      title: specialGroupUnfiltered?.translation || '',
      active: filterValue.length > 0,
      available: options.length > 0,
      value: filterValue,
      options,
      setValue: (value: string) => {
        const nextValue = allValues
          .filter((v) => !filterValue.includes(v))
          .concat(value);

        setFilterParams((previous) => ({
          ...previous,
          specials: nextValue,
        }));
      },
      clear: () => {
        // @TODO can't remove all, need to toggle the activeOptions?
        setFilterParams((previous) => ({
          ...previous,
          specials: null,
        }));
      },
    };
  };

  const hotelCategoriesToFilter = ({
    hotelCategoriesUnfiltered,
    hotelCategoriesFiltered,
  }: {
    hotelCategoriesUnfiltered: MetaCategories | undefined;
    hotelCategoriesFiltered: MetaCategories | undefined;
  }) => {
    const value = filterParams.categoryMin as
      | null
      | undefined
      | number
      | string;

    // static because unavailable options are missing in the response
    const options = [5, 4, 3, 2, 1]
      .map((category) => {
        const categoryUnfiltered =
          hotelCategoriesUnfiltered?.[
            `category${category}` as keyof MetaCategories
          ];
        const categoryFiltered =
          hotelCategoriesFiltered?.[
            `category${category}` as keyof MetaCategories
          ];

        return {
          value: String(category),
          label: '',
          count: categoryFiltered?.count || 0,
          priceMin: categoryUnfiltered?.priceDisplay,
          active: category === value,
          available: Number(categoryUnfiltered?.count) > 0, // @FIXME
        };
      })
      .concat({
        // @ts-expect-error TS2769: Workaround for "Any category" option
        value: null,
        label: '',
        count: metaFiltered?.totalCount || 0,
        priceMin: metaUnfiltered?.priceRange?.minDisplay,
        active: value === null,
        available: Number(metaFiltered?.totalCount) > 0,
      });

    return {
      id: FILTER_HOTEL_CATEGORY,
      title: '',
      value,
      active: value != null,
      available: options.some((option) => option.available),
      options,
      setValue: (nextValue: number | null) => {
        setFilterParams((previous) => ({
          ...previous,
          categoryMin: nextValue,
        }));
      },
      clear: () => {
        setFilterParams((previous) => ({
          ...previous,
          categoryMin: null,
        }));
      },
    };
  };

  const hotelRatingsToFilter = ({
    hotelRatingsUnfiltered,
    hotelRatingsFiltered,
  }: {
    hotelRatingsUnfiltered: MetaRatings | undefined;
    hotelRatingsFiltered: MetaRatings | undefined;
  }) => {
    const value = filterParams.ratingMin as
      | keyof MetaRatings
      | null
      | undefined;

    // Note that we don't use the provided "poor" rating
    const options = [
      RatingCategories.excellent,
      RatingCategories.very_good,
      RatingCategories.good,
      RatingCategories.fair,
    ]
      .map((rating) => {
        const ratingUnfiltered = hotelRatingsUnfiltered?.[rating];
        const ratingFiltered = hotelRatingsFiltered?.[rating];
        const threshold = getRatingCategoryThreshold(rating);
        const scoreMax = getRatingCategoryMaximum();

        return {
          value: rating,
          label: '',
          count: ratingFiltered?.count || 0,
          priceMin: ratingUnfiltered?.priceDisplay,
          active: rating === value,
          available: Number(ratingUnfiltered?.count) > 0, // @FIXME
          threshold,
          scoreMax,
        };
      })
      .concat({
        // @ts-expect-error TS2769: Workaround for "Any rating" option
        value: null,
        label: '',
        count: metaFiltered?.totalCount || 0,
        priceMin: metaUnfiltered?.priceRange?.minDisplay,
        active: value === null,
        available: Number(metaFiltered?.totalCount) > 0,
      });

    return {
      id: FILTER_HOTEL_RATING,
      title: '',
      value,
      active: value != null,
      available: options.some((option) => option.available),
      options,
      setValue: (nextValue: RatingCategory | null) => {
        setFilterParams((previous) => ({
          ...previous,
          ratingMin: nextValue,
        }));
      },
      clear: () => {
        setFilterParams((previous) => ({
          ...previous,
          ratingMin: null,
        }));
      },
    };
  };

  /**
   * We use the filter-data to render the filter chips (see: src/modules/common/hooks/useFilterChips.ts).
   * We also want to show filter chips for _all_ values provided via filterParams.
   * That means that we need all possible categories to exist in the filter-data,
   * even though they might not exist in the actual meta-data.
   * Typical example would be a attributes filter including "bananaboat" but a search for some winter destination.
   *
   * @FIXME
   * Technically the filter chips only depend on the filter values (from the URL).
   * So generating filters here that we know don't exist feels odd...
   * Getting and setting the filter values is probably it's own concern and should be extracted.
   * useFilterValues()-hook perhaps?
   */
  const metaUnfilteredMerged = merge(
    {
      attributes: mapValues(attributeFilterIdByItemType, () => ({})),
      tourOperatorSpecials: mapValues(
        tourOperatorSpecialFilterIdByItemType,
        () => ({})
      ),
    },
    metaUnfiltered || {}
  );

  // @ts-expect-error ...
  // eslint-disable-next-line sonarjs/function-return-type
  return Object.keys(metaUnfilteredMerged).flatMap((key) => {
    switch (key) {
      case 'attributes': {
        return Object.keys(metaUnfilteredMerged?.attributes || {}).map((type) =>
          attributeGroupToFilter({
            // @ts-expect-error ...
            id: filterIdByItemType[type],
            type,
            attributeGroupUnfiltered: metaUnfilteredMerged?.attributes?.[type],
            attributeGroupFiltered: metaFiltered?.attributes?.[type],
          })
        );
      }
      case 'tourOperatorSpecials': {
        return Object.keys(
          metaUnfilteredMerged?.tourOperatorSpecials || {}
        ).map((t) => {
          const type = t as PromotionCategory;
          return specialGroupToFilter({
            id: filterIdByItemType[type],
            type,
            specialGroupUnfiltered:
              metaUnfilteredMerged?.tourOperatorSpecials?.[type],
            specialGroupFiltered: metaFiltered?.tourOperatorSpecials?.[type],
          });
        });
      }
      case 'categories': {
        return hotelCategoriesToFilter({
          hotelCategoriesUnfiltered: metaUnfilteredMerged?.categories,
          hotelCategoriesFiltered: metaFiltered?.categories,
        });
      }
      case 'ratings': {
        return hotelRatingsToFilter({
          hotelRatingsUnfiltered: metaUnfilteredMerged?.ratings,
          hotelRatingsFiltered: metaFiltered?.ratings,
        });
      }
      default: {
        return [];
      }
    }
  });
};
